// Lightweight workout audio cues via the Web Audio API — tones are synthesized on
// the fly, so there are no asset files to load and latency is minimal. Respects a
// persisted mute preference. Browsers start the AudioContext suspended, so it must
// be unlocked from a user gesture (call unlockWorkoutAudio() on the Start tap).

const MUTE_KEY = 'probell:workoutSoundMuted';

let ctx: AudioContext | null = null;
let muted = false;

if (typeof window !== 'undefined') {
  try {
    muted = window.localStorage.getItem(MUTE_KEY) === '1';
  } catch {
    /* ignore */
  }
}

function getCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (!ctx) {
    const AC = window.AudioContext || (window as any).webkitAudioContext;
    if (!AC) return null;
    try {
      ctx = new AC();
    } catch {
      return null;
    }
  }
  return ctx;
}

/** Call from a user gesture so audio is allowed to play later (e.g. the Start tap). */
export function unlockWorkoutAudio(): void {
  const c = getCtx();
  if (c && c.state === 'suspended') void c.resume();
}

export function isWorkoutSoundMuted(): boolean {
  return muted;
}

export function setWorkoutSoundMuted(next: boolean): void {
  muted = next;
  try {
    window.localStorage.setItem(MUTE_KEY, next ? '1' : '0');
  } catch {
    /* ignore */
  }
  if (!next) unlockWorkoutAudio();
}

function tone(
  freq: number,
  durationMs: number,
  opts?: { type?: OscillatorType; gain?: number; delayMs?: number }
): void {
  if (muted) return;
  const c = getCtx();
  if (!c) return;
  if (c.state === 'suspended') void c.resume();
  const start = c.currentTime + (opts?.delayMs ?? 0) / 1000;
  const dur = durationMs / 1000;
  const peak = opts?.gain ?? 0.18;
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = opts?.type ?? 'sine';
  osc.frequency.setValueAtTime(freq, start);
  // Short attack/release envelope to avoid clicks.
  gain.gain.setValueAtTime(0, start);
  gain.gain.linearRampToValueAtTime(peak, start + 0.008);
  gain.gain.linearRampToValueAtTime(0, start + dur);
  osc.connect(gain);
  gain.connect(c.destination);
  osc.start(start);
  osc.stop(start + dur + 0.03);
}

function vibrate(pattern: number | number[]): void {
  if (muted) return;
  try {
    navigator.vibrate?.(pattern);
  } catch {
    /* ignore */
  }
}

/** Soft countdown tick — the last few seconds of an interval or the start countdown. */
export function cueTick(): void {
  tone(880, 90, { type: 'sine', gain: 0.13 });
  vibrate(20);
}

/**
 * The final 3·2·1 before an interval ends. Louder and longer than cueTick, and
 * rising in pitch, so the last seconds read as a countdown without looking at
 * the screen. Anything earlier than 3s falls back to the soft tick.
 */
const COUNTDOWN_FREQ: Record<number, number> = { 3: 784, 2: 880, 1: 988 };

export function cueCountdown(secondsLeft: number): void {
  const freq = COUNTDOWN_FREQ[secondsLeft];
  if (!freq) {
    cueTick();
    return;
  }
  tone(freq, 150, { type: 'triangle', gain: 0.22 });
  vibrate(40);
}

/** Clear "go" cue — a new EMOM interval / the workout starting (exercise changes now). */
export function cueGo(): void {
  tone(1318, 120, { type: 'square', gain: 0.16 });
  tone(1760, 170, { type: 'square', gain: 0.16, delayMs: 110 });
  vibrate(70);
}

/** Distinct lower "done" cue — a rest period ending or a method's time being up. */
export function cueEnd(): void {
  tone(660, 340, { type: 'triangle', gain: 0.2 });
  vibrate([40, 60, 40]);
}
